import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cx } from 'class-variance-authority';
import { ReactNode } from 'react';
import { Button } from './Button';

interface ModuleSetupGuideProps {
    icon: IconDefinition;
    title: string;
    description?: ReactNode;
    /** Numbered walkthrough of what happens after the action — omit for a one-step setup. */
    steps?: string[];
    /** Label of the primary action, e.g. "Bank verbinden". */
    actionLabel: string;
    onAction: () => void;
    isActionLoading?: boolean;
    /** False replaces the action with `permissionHint` — for users who may look but not set up. */
    canSetup?: boolean;
    permissionHint?: ReactNode;
    /** Rendered below the primary action, e.g. a link to the documentation. */
    footer?: ReactNode;
    className?: string;
}

/**
 * The "dieses Modul ist noch nicht eingerichtet" screen.
 *
 * Every screen of a module that needs a one-time setup renders this same component while the
 * setup is missing — identical wording and design everywhere — and `onAction` starts the setup
 * right there. That is deliberately different from an {@link EmptyState} pointing at a settings
 * page: forwarding costs a second click and dead-ends everyone without the manage permission,
 * which is what `canSetup` / `permissionHint` cover instead.
 *
 * Used by: Banking (`components/Banking/shared/ConnectBankCard.tsx`).
 */
export default function ModuleSetupGuide({
    icon,
    title,
    description,
    steps,
    actionLabel,
    onAction,
    isActionLoading = false,
    canSetup = true,
    permissionHint,
    footer,
    className
}: ModuleSetupGuideProps) {
    return (
        <div
            className={cx(
                // Fills the module content area and centers in it — the setup is the only thing
                // on the page, so it belongs in the middle rather than pinned under the header.
                'tw-flex tw-min-h-full tw-w-full tw-flex-1 tw-flex-col tw-items-center tw-justify-center',
                'tw-gap-6 tw-px-6 tw-py-14 tw-text-center',
                className
            )}
        >
            <div className="tw-flex tw-size-16 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-accents-1">
                <FontAwesomeIcon icon={icon} className="tw-text-3xl tw-text-accents-6" />
            </div>

            <div>
                <h2 className="tw-mb-2 tw-text-xl tw-font-bold tw-text-accents-9">{title}</h2>
                {description && (
                    <p className="tw-mx-auto tw-mb-0 tw-max-w-sm tw-text-sm tw-text-accents-6">{description}</p>
                )}
            </div>

            {steps && steps.length > 0 && (
                <ol className="tw-mx-auto tw-mb-0 tw-flex tw-max-w-sm tw-list-none tw-flex-col tw-gap-3 tw-pl-0 tw-text-left">
                    {steps.map((step, i) => (
                        <li key={step} className="tw-flex tw-items-start tw-gap-3">
                            <span className="tw-flex tw-size-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-accents-1 tw-text-xs tw-font-semibold tw-text-accents-7">
                                {i + 1}
                            </span>
                            <span className="tw-mt-0.5 tw-text-sm tw-text-accents-7">{step}</span>
                        </li>
                    ))}
                </ol>
            )}

            {canSetup ? (
                <Button color="success" onPress={onAction} isLoading={isActionLoading}>
                    {actionLabel}
                </Button>
            ) : (
                permissionHint && (
                    <div className="tw-flex tw-max-w-sm tw-items-start tw-gap-3 tw-rounded-xl tw-bg-accents-1 tw-p-4 tw-text-left tw-text-sm tw-text-accents-7">
                        <FontAwesomeIcon icon={faLock} className="tw-mt-0.5 tw-shrink-0 tw-text-accents-5" />
                        <span>{permissionHint}</span>
                    </div>
                )
            )}

            {footer}
        </div>
    );
}
